import { getErrorMessage } from "utils/error";
import { getSourceLabel } from "utils/getSourceLabel";
import addressApi from "api/landlord/address";
import { useNavigate } from "react-router-dom";
import {
  type CellClickedEvent,
  type ColDef,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type SortChangedEvent
} from "ag-grid-community";
import type { SortingState } from "@tanstack/react-table";
import { MagicLink, Unit, UnitPriceRange } from "types/landlord/address";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "providers/Auth";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { unitStatus } from "shared/app/UnitsTableView/constants";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import {
  ContextMenuPopover,
  type Action
} from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { useSettingsController } from "views/settings-integration/controller";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { formatToCurrency } from "shared/utils";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { UnitAddressValidationCell } from "shared/app/AddressValidationStatus/UnitAddressValidationCell";
import { PartnerFeatures } from "shared/types/partner";
import { RentAmount } from "../../../../components/RentAmount";
import { Property, PropertyKind } from "shared/types";
import { TemplateLinksSubmenu } from "shared/components/TemplateLinksSubmenu/TemplateLinksSubmenu";
import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";
import { useLayout } from "providers/Layout/hooks";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import {
  AlertCircleIcon,
  Door02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Search01Icon } from "@hugeicons-pro/core-solid-rounded";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";

/**
 * `unit_price_range` arrives as whole dollars rather than the `Money` DTO the
 * rent columns use, so it is converted before formatting instead of being
 * handed to `RentAmount` (which reads `.cents`) and rendering 100x low.
 */
const formatPriceRange = ({ min, max }: UnitPriceRange) => {
  const format = (dollars: number) =>
    formatToCurrency({ cents: dollars * 100, currency: "USD" });

  return min === max ? format(min) : `${format(min)} - ${format(max)}`;
};

type Props = {
  currentProperty?: Property;
  data: Unit[];
  handleCopyLink: (link: string) => void;
  setSelectedUnitId: (id: number) => void;
  setShowInviteApplicantsModal: (show: boolean) => void;
  setShowDeleteUnitModal: (show: boolean) => void;
  hasSearchParams?: boolean;
  hideProperty?: boolean;
  isLoading?: boolean;
  handleAvailableForApplication: (unit: Unit) => void;
  sorting?: SortingState;
  setSorting?: (sorting: SortingState) => void;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
  onAddUnit?: () => void;
  hiddenColumnIds?: Set<string>;
};

const getEmptyStateText = ({
  currentProperty,
  hasSearchParams,
  unitsDictionary,
  propertyDictionary
}: {
  currentProperty?: Property;
  hasSearchParams?: boolean;
  unitsDictionary: string;
  propertyDictionary: string;
}) => {
  if (hasSearchParams) return "We couldn't find any data matching your query";

  if (currentProperty?.kind === PropertyKind.MultiFamily)
    return `Add ${unitsDictionary} to the mutil family ${propertyDictionary}`;

  return `Add ${unitsDictionary} to the ${propertyDictionary}`;
};

function isCopyApplicationLinkDisabled(unit: Unit): boolean {
  return unit.listed ? !unit.magic_link?.active : false;
}

function getBoomScreenDeleteTooltip(
  unit: Unit,
  unitDictionary: string
): string | undefined {
  if (unit.pms_id) return `You can't delete a PMS ${unitDictionary}`;
  if (!unit.can_delete)
    return `You can't delete a ${unitDictionary} with applications`;
  return undefined;
}

// Mirrors ListingSection's CLEARED_SYNDICATION: unlisting a unit also turns
// off every syndication feed, matching the detail view's disableShowOnPortfolio.
const CLEARED_SYNDICATION_PAYLOAD = {
  feed_zillow_enabled: false,
  feed_zillow_spotlight_enabled: false,
  feed_apartments_com_enabled: false,
  feed_apartments_com_variant: null,
  feed_zumper_enabled: false,
  feed_dwellsy_enabled: false,
  feed_affordable_housing_com_enabled: false,
  feed_rental_beast_enabled: false
} as const;

type ListedToggleCellProps = {
  row: Unit;
  isEverySyncEnabled: boolean;
  onUpdated: (unit: Unit) => void;
};

// Requires the sibling boompay-api PR exposing `pms_overrides` on the units
// list entity — without it, `row.pms_overrides` is always undefined and every
// PMS-synced unit's toggle safely (but overly conservatively) renders locked.
const ListedToggleCell: FC<ListedToggleCellProps> = ({
  row,
  isEverySyncEnabled,
  onUpdated
}) => {
  const [isPending, setIsPending] = useState(false);
  const magicLinkData = row.magic_link as MagicLink | null;
  const listed = row.listed ?? false;
  const active = magicLinkData?.active ?? false;
  const id = magicLinkData?.id;
  const listedSynced = row.pms_overrides?.listed ?? true;
  const availableForApplicationSynced =
    row.pms_overrides?.available_for_application ?? true;
  // Mirrors ListingSection's listedToggleLocked: enabling Listed while
  // applications are off would force-enable a PMS-synced availability
  // field, so lock it until that field is edited manually.
  const listedToggleLocked =
    !listed && !active && availableForApplicationSynced;
  const pmsLocked = Boolean(
    isEverySyncEnabled && row.pms_id && (listedSynced || listedToggleLocked)
  );

  if (pmsLocked)
    return (
      <AllowOrDisallowApplications
        isActive={listed}
        withText={false}
        variant="listed"
      />
    );

  return (
    <Switch
      size={SwitchSize.SM}
      checked={listed}
      disabled={!id || isPending}
      onChange={async (nextListed) => {
        if (!id) return;
        setIsPending(true);
        try {
          const res = await addressApi.updateMagicLink(id, {
            listed: nextListed,
            active: nextListed ? true : undefined,
            ...(nextListed ? {} : CLEARED_SYNDICATION_PAYLOAD)
          });
          onUpdated(res.unit);
        } catch (err) {
          toast.error(getErrorMessage(err));
        } finally {
          setIsPending(false);
        }
      }}
    />
  );
};

const ADDRESS_VALIDATION_COLUMN: ColDef<Unit> = {
  headerName: "Address validation",
  colId: "address_validation",
  width: 190,
  minWidth: 160,
  resizable: true,
  cellRenderer: (params: ICellRendererParams<Unit>) =>
    params.data ? <UnitAddressValidationCell unit={params.data} /> : null
};

export const UnitsTable: FC<Props> = ({
  currentProperty,
  handleAvailableForApplication,
  data,
  handleCopyLink,
  setSelectedUnitId,
  setShowInviteApplicantsModal,
  setShowDeleteUnitModal,
  hasSearchParams,
  hideProperty,
  isLoading,
  sorting = [],
  setSorting,
  pagination,
  perPageRows,
  viewPortHeightOffset,
  onAddUnit,
  hiddenColumnIds
}) => {
  const { partnerFeatures } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const {
    units,
    unit: unitDictionary,
    Unit,
    Property,
    property,
    Units
  } = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const { type, pms } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const addressValidationColumns = useMemo(
    () => (type.isAddressValidationEnabled ? [ADDRESS_VALIDATION_COLUMN] : []),
    [type.isAddressValidationEnabled]
  );
  const { isEverySyncEnabled } = useSettingsController();
  const { sectionHeight } = useLayout();
  const gridApiRef = useRef<GridApi<Unit> | null>(null);
  const [pendingUnlistedLink, setPendingUnlistedLink] = useState<string | null>(
    null
  );

  const handleCopyApplicationLink = useCallback(
    (unit: Unit, link: string) => {
      setSelectedUnitId(unit.id);
      if (!unit.listed) {
        setPendingUnlistedLink(link);
        return;
      }
      handleCopyLink(link);
    },
    [handleCopyLink, setSelectedUnitId]
  );

  const lastMiddleColId = type.isBoomScreen ? "listed" : "occupied";

  const columns = useMemo<ColDef<Unit>[]>(() => {
    const ret: ColDef<Unit>[] = [];

    if (!hideProperty)
      ret.push({
        headerName: Property,
        field: "property",
        colId: "property",
        pinned: "left",
        width: 330,
        minWidth: 180,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col justify-center gap-0.5">
              <p className="text-sm font-medium text-content-1000 truncate">
                {row.property.name}
              </p>
              <p className="text-xs text-content-400 truncate">
                {getShortAddressFromAddress(row.property.address)}
              </p>
            </div>
          );
        }
      });

    ret.push({
      headerName: Unit,
      field: "title",
      colId: "title",
      pinned: hideProperty ? "left" : undefined,
      width: 200,
      minWidth: 150,
      resizable: true,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<Unit>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <p className="font-medium text-content-1000 truncate max-w-[460px]">
            {row.title}
          </p>
        );
      }
    });

    ret.push({
      headerName: "Property group",
      field: "property.property_group",
      colId: "property_group",
      width: 150,
      minWidth: 100,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<Unit>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <p className="text-content-500">
            {row.property.property_group?.name || "--"}
          </p>
        );
      }
    });

    ret.push({
      headerName: homeSiteRentEnabled ? "Total rent" : "Rent amount",
      field: "monthly_rent_amount",
      colId: "monthly_rent_amount",
      width: 120,
      minWidth: 80,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<Unit>) => {
        const row = params.data;
        if (!row) return null;

        // A dynamically-priced unit has no single rent: the amount is only
        // decided once an applicant picks a move-in date and lease term, so
        // show the span the matrix currently quotes instead of the flat value.
        // The PMS owns that price, so the override note never applies here.
        if (row.unit_price_range)
          return (
            <div className="flex flex-col justify-center gap-0.5">
              <p className="font-medium text-content-1000 leading-normal">
                {formatPriceRange(row.unit_price_range)}
              </p>
              <p className="text-xs text-content-400 truncate">
                Dynamic pricing
              </p>
            </div>
          );

        return (
          <div className="flex flex-col justify-center gap-0.5">
            <RentAmount
              value={row.monthly_rent_amount}
              className="leading-normal"
            />
            {!row.pms_monthly_rent_amount_override &&
            row.pms_monthly_rent_amount?.cents !==
              row.monthly_rent_amount?.cents ? (
              <p className="text-xs text-content-400 truncate">
                Manually overridden
              </p>
            ) : null}
          </div>
        );
      }
    });

    if (homeSiteRentEnabled) {
      ret.push({
        headerName: "Home rent",
        field: "home_rent_amount",
        colId: "home_rent_amount",
        width: 150,
        minWidth: 100,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col justify-center gap-0.5">
              <RentAmount
                value={row.home_rent_amount}
                className="leading-normal"
              />
              {!row.pms_home_rent_amount_override &&
              row.pms_home_rent_amount?.cents !==
                row.home_rent_amount?.cents ? (
                <p className="text-xs text-content-400 truncate">
                  Manually overridden
                </p>
              ) : null}
            </div>
          );
        }
      });
      ret.push({
        headerName: "Site rent",
        field: "site_rent_amount",
        colId: "site_rent_amount",
        width: 150,
        minWidth: 100,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="flex flex-col justify-center gap-0.5">
              <RentAmount
                value={row.site_rent_amount}
                className="leading-normal"
              />
              {!row.pms_site_rent_amount_override &&
              row.pms_site_rent_amount?.cents !==
                row.site_rent_amount?.cents ? (
                <p className="text-xs text-content-400 truncate">
                  Manually overridden
                </p>
              ) : null}
            </div>
          );
        }
      });
    }

    if (hasPropertyOwner)
      ret.push({
        headerName: "Owner",
        field: "property.owner",
        colId: "owner",
        width: 110,
        minWidth: 90,
        resizable: true,
        cellClass: "truncate",
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <p className="text-content-500">{row.property.owner || "--"}</p>
          );
        }
      });

    if (type.isPMS)
      ret.push({
        headerName: "Source",
        field: "pms_id",
        colId: "pms_id",
        width: type.isMultiPMS ? 200 : 140,
        minWidth: type.isMultiPMS ? 200 : 100,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          return <p className="text-content-500">{getSourceLabel(row)}</p>;
        }
      });

    ret.push(...addressValidationColumns);

    ret.push({
      headerName: "Leads",
      colId: "leads",
      width: 91,
      minWidth: 91,
      resizable: true,
      valueGetter: (params) => params.data?.leads_last_7_days ?? 0
    });

    ret.push({
      headerName: "Lockbox",
      colId: "lockbox",
      width: 131,
      minWidth: 131,
      resizable: true,
      valueGetter: (params) =>
        params.data?.lockbox_status?.assigned
          ? (params.data.lockbox_status.vendor ?? "Assigned")
          : "—"
    });

    ret.push({
      headerName: "Current status",
      field: "occupied",
      colId: "occupied",
      width: 140,
      minWidth: 120,
      flex: "occupied" === lastMiddleColId ? 1 : undefined,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<Unit>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div>
            <Badge
              size={BadgeSize.SM}
              variant={
                row.occupied ? BadgeVariant.PRIMARY : BadgeVariant.SUCCESS
              }
            >
              {unitStatus(row.occupied)}
            </Badge>
            {!row.pms_occupied_override && row.pms_occupied !== row.occupied ? (
              <p className="text-xs text-content-400 truncate">
                Manually overridden
              </p>
            ) : null}
          </div>
        );
      }
    });

    if (type.isBoomScreen)
      ret.push({
        headerName: "Allow applications",
        field: "magic_link",
        colId: "magic_link",
        width: 140,
        minWidth: 140,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          const magicLinkData = row.magic_link as MagicLink | null;
          const active = magicLinkData?.active || false;
          const id = magicLinkData?.id || 0;
          const availableForApplicationSynced =
            row.pms_overrides?.available_for_application ?? true;
          const listedSynced = row.pms_overrides?.listed ?? true;
          // Mirrors ListingSection's allowApplyingToggleLocked: disabling
          // applications on an active+listed unit would force-clear a
          // PMS-synced listed field, so lock it until that field is edited
          // manually.
          const allowApplyingToggleLocked =
            active && (row.listed ?? false) && listedSynced;
          const pmsLocked = Boolean(
            isEverySyncEnabled &&
            row.pms_id &&
            (availableForApplicationSynced || allowApplyingToggleLocked)
          );
          return pmsLocked ? (
            <AllowOrDisallowApplications isActive={active} withText={false} />
          ) : (
            <Switch
              size={SwitchSize.SM}
              checked={active}
              onChange={async (active) => {
                try {
                  const listed =
                    row.property.kind === null
                      ? undefined
                      : active && row.listed;
                  const res = await addressApi.updateMagicLink(id, {
                    active,
                    listed
                  });
                  handleAvailableForApplication(res.unit);
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
            />
          );
        }
      });

    if (type.isBoomScreen)
      ret.push({
        headerName: "List unit",
        field: "listed",
        colId: "listed",
        width: 100,
        minWidth: 100,
        flex: "listed" === lastMiddleColId ? 1 : undefined,
        resizable: true,
        cellRenderer: (params: ICellRendererParams<Unit>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <ListedToggleCell
              row={row}
              isEverySyncEnabled={isEverySyncEnabled}
              onUpdated={handleAvailableForApplication}
            />
          );
        }
      });

    ret.push({
      headerName: "",
      field: "id",
      colId: "actions",
      width: 72,
      minWidth: 72,
      pinned: "right",
      suppressMovable: true,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<Unit>) => {
        const unit = params.data;
        if (!unit) return null;

        const popoverActions: Action[] = [
          {
            title: "Review",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              navigate(`/properties/${unit.property.id}/units/${unit.id}`);
            }
          }
        ];

        if (type.isBoomScreen) {
          popoverActions.push({
            title: `Invite to ${unitDictionary}`,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              setSelectedUnitId(unit.id);
              setShowInviteApplicantsModal(true);
            }
          });
          const isCopyLinkDisabled = isCopyApplicationLinkDisabled(unit);
          popoverActions.push({
            title: (
              <TemplateLinksSubmenu
                variant="COMPACT"
                disabled={isCopyLinkDisabled}
                onCopyLink={(link) => handleCopyApplicationLink(unit, link)}
                defaultLink={unit.magic_link?.application_link ?? ""}
                unitId={unit.id ? String(unit.id) : undefined}
              />
            ),
            disabled: isCopyLinkDisabled,
            tooltip: isCopyLinkDisabled
              ? `When a ${unitDictionary} is not accepting applications, applicants can only be invited via email`
              : undefined,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            }
          });
          popoverActions.push({
            title: `Delete ${unitDictionary}`,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              if (unit.can_delete && !unit.pms_id) {
                setShowDeleteUnitModal(true);
                setSelectedUnitId(unit.id);
              }
            },
            color:
              unit.can_delete && !unit.pms_id
                ? "text-danger-500"
                : "text-content-100",
            disabled: !unit.can_delete || !!unit.pms_id,
            tooltip: getBoomScreenDeleteTooltip(unit, unitDictionary)
          });
        } else
          popoverActions.push({
            title: `Delete ${unitDictionary}`,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              setShowDeleteUnitModal(true);
              setSelectedUnitId(unit.id);
            },
            color:
              unit.can_delete || !type.isPMS
                ? "text-content-100"
                : "text-danger-500",
            disabled: !unit.can_delete || !type.isPMS,
            tooltip: type.isPMS
              ? `You can't delete a ${unitDictionary}`
              : `You can't delete a ${unitDictionary} with applications`
          });

        return (
          <ContextMenuPopover
            placement="bottom-start"
            actions={popoverActions}
          />
        );
      }
    });

    return ret;
  }, [
    type.isBoomScreen,
    type.isPMS,
    type.isMultiPMS,
    hasPropertyOwner,
    hideProperty,
    homeSiteRentEnabled,
    addressValidationColumns,
    isEverySyncEnabled,
    lastMiddleColId,
    Unit,
    Property,
    unitDictionary,
    handleAvailableForApplication,
    setSelectedUnitId,
    setShowInviteApplicantsModal,
    setShowDeleteUnitModal,
    handleCopyApplicationLink,
    navigate
  ]);

  const visibleColumns = useMemo(
    () =>
      hiddenColumnIds?.size
        ? columns.filter((column) => !hiddenColumnIds.has(String(column.colId)))
        : columns,
    [columns, hiddenColumnIds]
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<Unit>) => String(params.data.id),
    []
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent<Unit>) => {
      const row = event.data;
      if (!row) return;
      const colId = event.column.getColId();
      if (colId === "actions" || colId === "magic_link" || colId === "listed")
        return;
      navigate(`/properties/${row.property.id}/units/${row.id}`);
    },
    [navigate]
  );

  const onGridReady = useCallback((params: GridReadyEvent<Unit>) => {
    gridApiRef.current = params.api;
  }, []);

  const onSortChanged = useCallback(
    (event: SortChangedEvent<Unit>) => {
      if (!setSorting) return;
      const colState = event.api.getColumnState();
      const sortedCol = colState.find((c) => c.sort);
      if (sortedCol && sortedCol.colId)
        setSorting([{ id: sortedCol.colId, desc: sortedCol.sort === "desc" }]);
      else setSorting([]);
    },
    [setSorting]
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const columnState = sorting.map((s) => ({
      colId: s.id,
      sort: (s.desc ? "desc" : "asc") as "asc" | "desc"
    }));
    api.applyColumnState({
      state: columnState,
      defaultState: { sort: null }
    });
  }, [sorting]);

  const emptyStateText = getEmptyStateText({
    currentProperty,
    hasSearchParams,
    unitsDictionary: units,
    propertyDictionary: property
  });

  return (
    <>
      <AgGrid<Unit>
        themeOverrides={{ "--ag-cell-horizontal-padding": "16px" }}
        columns={visibleColumns}
        rows={data}
        loading={isLoading}
        footer={pagination}
        showFooter={!!pagination}
        perPageRows={perPageRows}
        viewPortHeight={sectionHeight}
        viewPortHeightOffset={
          viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
        }
        getRowId={getRowId}
        onCellClicked={onCellClicked}
        onGridReady={onGridReady}
        onSortChanged={onSortChanged}
        noRowsFallback={
          <NoRowsFallback
            Icon={hasSearchParams ? Search01Icon : Door02Icon}
            title={
              hasSearchParams ? "No results found" : `${Units} will appear here`
            }
            text={emptyStateText}
            buttonText={
              !hasSearchParams && onAddUnit ? `Add ${Unit}` : undefined
            }
            buttonAction={!hasSearchParams ? onAddUnit : undefined}
          />
        }
      />

      <ConfirmDialog
        opened={pendingUnlistedLink !== null}
        onClose={() => setPendingUnlistedLink(null)}
        onConfirm={() => {
          if (pendingUnlistedLink) handleCopyLink(pendingUnlistedLink);
          setPendingUnlistedLink(null);
        }}
        title={`${Unit} not listed`}
        description={`This ${unitDictionary} isn't listed yet. The link you copied won't work for applicants — for internal use only.`}
        variant="primary"
        icon={{ icon: AlertCircleIcon, variant: "primary" }}
        confirmButton={{ label: "Copy link" }}
      />
    </>
  );
};
